import React, { useEffect, useState } from "react";
import ActionButton from "../components/ActionButton.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";
import "./Settings.css";

const Settings = () => {
  const navigate = useNavigate();
  const [interestRate, setInterestRate] = useState<number | null>(null);
  const [retirementDate, setRetirementDate] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Fetch the interest rate from the database
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        if (authData && authData.user) {
          setUserEmail(authData.user.email ?? null);
        }

        const { data, error } = await supabase
          .from("users")
          .select("interest_rate, retirement_date")
          .single(); // Assuming the current user is already authenticated

        if (error) {
          console.error("Error fetching interest rate:", error);
        } else {
          setInterestRate(data.interest_rate);
          setRetirementDate(data.retirement_date);
        }
      } catch (error) {
        console.error("Unexpected error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  // Update the interest rate in the database
  const handleInterestRateChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRate = parseFloat(event.target.value);
    setInterestRate(newRate);

    try {
      const user = await supabase.auth.getUser(); // Get the current authenticated user
      if (!user) {
        console.error("No authenticated user found.");
        return;
      }

      if (!user.data.user) {
        console.error("Authenticated user data is null.");
        return;
      }

      const { error } = await supabase
        .from("users")
        .update({ interest_rate: newRate })
        .eq("user_id", user.data.user.id)
        .single();

      if (error) {
        console.error("Error updating interest rate:", error);
      }
    } catch (error) {
      console.error("Unexpected error updating interest rate:", error);
    }
  };

  const handleRetirementDateChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const yearValue = event.target.value;
    // Format as YYYY-01-01 (January 1st of the selected year)
    const newDate = `${yearValue}-01-01`;
    setRetirementDate(newDate);

    try {
      const user = await supabase.auth.getUser(); // Get the current authenticated user
      if (!user) {
        console.error("No authenticated user found.");
        return;
      }

      if (!user.data.user) {
        console.error("Authenticated user data is null.");
        return;
      }

      const { error } = await supabase
        .from("users")
        .update({ retirement_date: newDate })
        .eq("user_id", user.data.user.id)
        .single();

      if (error) {
        console.error("Error updating retirement date:", error);
      }
    } catch (error) {
      console.error("Unexpected error updating retirement date:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/receipt-library"); // Redirect to the landing page after logout
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const handleManageSubscription = () => {
    const baseUrl = "https://billing.stripe.com/p/login/6oEbJB9Xmdl55fG8ww";
    const urlWithEmail = userEmail
      ? `${baseUrl}?prefilled_email=${encodeURIComponent(userEmail)}`
      : baseUrl;

    window.open(urlWithEmail, "_blank");
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Settings</h1>
            <p>Update your account settings.</p>
          </div>
          <ActionButton
            text="Logout"
            onClick={handleLogout}
            responsive={false}
            secondary={true}
          />
        </div>
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Interest Rate</h2>
            <p>Set the assumed interest rate for your HSA account.</p>
          </div>
          <div className="settings-label-input">
            <input
              type="number"
              value={interestRate !== null ? interestRate : ""}
              onChange={handleInterestRateChange}
              placeholder="8"
            />
            <h2>%</h2>
          </div>
        </div>
        {/* <div className="settings-label">
          <div className="container-label-header">
            <h2>Expected Retirement Date</h2>
            <p>Set your expected retirement date.</p>
          </div>
          <div className="settings-label-input">
            <input
              type="number"
              value={retirementDate ? retirementDate.split("-")[0] : ""}
              onChange={handleRetirementDateChange}
              placeholder="2050"
              min="2000"
              max="2100"
            />
          </div>
        </div> */}
        <div className="settings-label">
          <div className="container-label-header">
            <h2>Subscription</h2>
            <p>Manage your Shoebox Plus subscription.</p>
          </div>
          <ActionButton
            text="Manage"
            onClick={handleManageSubscription}
            secondary={true}
          />
        </div>
      </div>
    </div>
  );
};

export default Settings;
